import { useEffect, useState } from 'react';
import { AuthGuard } from './components/AuthGuard';
import { Layout } from './components/Layout';
import { CommandPalette } from './components/CommandPalette';
import { type View, VIEW_LABEL } from './lib/views';
// Pages live (existantes)
import { InvestorsPage } from './pages/Investors';
import { DashboardPage } from './pages/Dashboard';
import { NewsPage } from './pages/News';
import { FoundersPage, type FoundersPreFilter } from './pages/Founders';
import { SettingsPage } from './pages/Settings';
// Pages stubs (PR 1, à implémenter en PR 1.x)
import { FoundersInboxPage } from './pages/FoundersInbox';
import { ProgrammesPage } from './pages/Programmes';
import { BrickPipelinePage } from './pages/BrickPipeline';
import { VeillePage } from './pages/Veille';
import { TemplatesPage } from './pages/Templates';
import { LeadsPage } from './pages/Leads';
import { EmailFunnelsPage } from './pages/EmailFunnels';
import { QuantumCompaniesPage } from './pages/QuantumCompanies';
import { ActivityLogPage } from './pages/ActivityLog';

export default function App() {
  return (
    <AuthGuard>
      {(user) => <Inner user={user} />}
    </AuthGuard>
  );
}

function Inner({ user }: { user: { email: string; full_name?: string } }) {
  const [view, setView] = useState<View>('investors-directory');
  const [fundsCount, setFundsCount] = useState(0);
  const [programmesCount, setProgrammesCount] = useState(0);
  const [toRefreshCount, setToRefreshCount] = useState(0);
  const [findingsCount, setFindingsCount] = useState(0);
  const [pendingHighCranCount, setPendingHighCranCount] = useState(0);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [nouveauSubmissionsCount, setNouveauSubmissionsCount] = useState(0);
  const [leadsCount, setLeadsCount] = useState(0);
  const [hotLeadsCount, setHotLeadsCount] = useState(0);
  const [pendingChanges, setPendingChanges] = useState(0);
  const [draftNewsCount, setDraftNewsCount] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Drilldowns depuis Dashboard pré-positionnent un filtre sur Founders.
  const [foundersPreFilter, setFoundersPreFilter] = useState<FoundersPreFilter | null>(null);

  function navigateToFounders(filter: FoundersPreFilter) {
    setFoundersPreFilter(filter);
    setView('founders');
  }

  // ⌘K / Ctrl+K ouvre la command palette globalement.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Slots par view pour count + pip dans la sidebar.
  // Hydratés depuis chaque page qui charge ses données : InvestorsPage,
  // ProgrammesPage, NewsPage (via draftNewsCount). Les autres slots restent
  // vides tant que la PR correspondante n'est pas faite.
  const slotsByView = {
    'investors-directory': { count: fundsCount },
    'non-dilutive': { count: programmesCount },
    'brick-pipeline': toRefreshCount > 0
      ? { count: toRefreshCount, pip: { color: 'gold' as const, label: `${toRefreshCount} à refresh` } }
      : { count: toRefreshCount },
    'veille': pendingHighCranCount > 0
      ? { count: findingsCount, pip: { color: 'gold' as const, label: `${pendingHighCranCount} pending cran 3` } }
      : { count: findingsCount },
    'founders-inbox': nouveauSubmissionsCount > 0
      ? { count: submissionsCount, pip: { color: 'red' as const, label: `${nouveauSubmissionsCount} nouveaux` } }
      : { count: submissionsCount },
    'leads': hotLeadsCount > 0
      ? { count: leadsCount, pip: { color: 'red' as const, label: `${hotLeadsCount} Hot` } }
      : { count: leadsCount },
  };

  return (
    <>
      <Layout
        user={user}
        view={view}
        setView={setView}
        slotsByView={slotsByView}
        pendingChanges={pendingChanges}
        draftNewsCount={draftNewsCount}
        section={VIEW_LABEL[view]}
        onPublished={() => setPendingChanges(0)}
        onSearchClick={() => setPaletteOpen(true)}
      >
        {view === 'investors-directory' && (
          <InvestorsPage onCountChange={setFundsCount} onDirty={setPendingChanges} />
        )}
        {view === 'founders' && (
          <FoundersPage preFilter={foundersPreFilter} clearPreFilter={() => setFoundersPreFilter(null)} />
        )}
        {view === 'founders-inbox' && (
          <FoundersInboxPage
            onCountChange={setSubmissionsCount}
            onNouveauCountChange={setNouveauSubmissionsCount}
          />
        )}
        {view === 'non-dilutive' && <ProgrammesPage onCountChange={setProgrammesCount} />}
        {view === 'brick-pipeline' && (
          <BrickPipelinePage
            onRefreshCountChange={setToRefreshCount}
            onProgrammesCountChange={setProgrammesCount}
          />
        )}
        {view === 'veille' && (
          <VeillePage
            onCountChange={setFindingsCount}
            onPendingHighCranChange={setPendingHighCranCount}
          />
        )}
        {view === 'templates' && <TemplatesPage />}
        {view === 'leads' && (
          <LeadsPage
            onCountChange={setLeadsCount}
            onHotCountChange={setHotLeadsCount}
          />
        )}
        {view === 'email-funnels' && <EmailFunnelsPage />}
        {view === 'news' && <NewsPage onDraftCountChange={setDraftNewsCount} />}
        {view === 'quantum-companies' && <QuantumCompaniesPage />}
        {view === 'dashboard' && <DashboardPage onDrillToFounders={navigateToFounders} />}
        {view === 'activity' && <ActivityLogPage />}
        {view === 'settings' && <SettingsPage />}
      </Layout>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} navigate={setView} />
    </>
  );
}
